import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import HttpService from '../../../services/httpService';
import { setLoading, setSnackInfo } from '../../../redux/actions/appAction';

function parseDepartmentList(response) {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.departments)) return raw.departments;
  return [];
}

const defaultForm = {
  name: '',
  kekaDepartmentId: '',
};

function KekaRoles(props) {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editId, setEditId] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [form, setForm] = useState(defaultForm);

  const departmentOptions = useMemo(
    () =>
      departments.map((d) => ({
        _id: d?._id,
        name: d?.name || '',
      })),
    [departments]
  );

  const loadPageData = async () => {
    props.setLoading(true);
    try {
      const [rolesRes, departmentsRes] = await Promise.all([
        HttpService.getKekaRoles(),
        HttpService.getKekaDepartments(),
      ]);
      const roleList = Array.isArray(rolesRes?.data) ? rolesRes.data : [];
      const deptList = parseDepartmentList(departmentsRes);
      setRows(roleList);
      setDepartments(deptList);
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading keka roles',
        'error'
      );
      setRows([]);
      setDepartments([]);
    } finally {
      props.setLoading(false);
    }
  };

  useEffect(() => {
    void loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreate = () => {
    setEditId('');
    setForm(defaultForm);
    setOpenForm(true);
  };

  const handleOpenEdit = (item) => {
    setEditId(item?._id || '');
    setForm({
      name: item?.name || '',
      kekaDepartmentId:
        item?.kekaDepartmentId?._id ||
        item?.kekaDepartmentId ||
        '',
    });
    setOpenForm(true);
  };

  const validateForm = () => {
    if (!String(form.name || '').trim()) return 'Role name is required';
    if (!String(form.kekaDepartmentId || '').trim()) {
      return 'Keka department is required';
    }
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      props.setSnackInfo(validationError, 'error');
      return;
    }

    props.setLoading(true);
    try {
      const payload = {
        name: String(form.name || '').trim(),
        kekaDepartmentId: form.kekaDepartmentId,
      };
      if (editId) {
        await HttpService.updateKekaRole(editId, payload);
        props.setSnackInfo('Keka role updated successfully', 'success');
      } else {
        await HttpService.createKekaRole(payload);
        props.setSnackInfo('Keka role created successfully', 'success');
      }
      setOpenForm(false);
      setEditId('');
      setForm(defaultForm);
      await loadPageData();
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error saving keka role',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    props.setLoading(true);
    try {
      await HttpService.deleteKekaRole(deleteId);
      props.setSnackInfo('Keka role deactivated', 'success');
      setOpenDelete(false);
      setDeleteId('');
      await loadPageData();
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error deleting keka role',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Keka Roles
        </Typography>
        <Button
          variant="contained"
          style={{ backgroundColor: '#F47E46', color: '#fff' }}
          onClick={handleOpenCreate}
        >
          Add Keka Role
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No keka roles found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row?._id || index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row?.name || '-'}</TableCell>
                  <TableCell>{row?.kekaDepartmentId?.name || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        style={{ color: '#d32f2f' }}
                        onClick={() => {
                          setDeleteId(row?._id || '');
                          setOpenDelete(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editId ? 'Edit Keka Role' : 'Add Keka Role'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gridGap={16}>
            <TextField
              label="Role Name"
              variant="outlined"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              label="Keka Department"
              variant="outlined"
              size="small"
              fullWidth
              select
              value={form.kekaDepartmentId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, kekaDepartmentId: e.target.value }))
              }
            >
              <MenuItem value="">
                <em>Select Department</em>
              </MenuItem>
              {departmentOptions.map((d) => (
                <MenuItem key={d._id} value={d._id}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Keka Role</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to deactivate this role?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default connect(null, { setLoading, setSnackInfo })(KekaRoles);
